import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { LuChevronLeft, LuChevronRight, LuCalendar, LuClock, LuUser, LuMapPin } from 'react-icons/lu';
import PageHeader from '../shared/PageHeader';
import Button from '../../components/common/Button';
import { appointmentsApi, courtCasesApi, tasksApi, extractList } from '../../api/facades';

type IconProps = { className?: string };
const ChevronLeftIcon = LuChevronLeft as React.ComponentType<IconProps>;
const ChevronRightIcon = LuChevronRight as React.ComponentType<IconProps>;
const CalendarIcon = LuCalendar as React.ComponentType<IconProps>;
const ClockIcon = LuClock as React.ComponentType<IconProps>;
const UserIcon = LuUser as React.ComponentType<IconProps>;
const MapPinIcon = LuMapPin as React.ComponentType<IconProps>;

interface Appointment {
  id: number;
  name?: string;
  type?: string;
  date?: string;
  time?: string;
  client?: { name?: string };
  advo_client_id?: number;
  is_active?: string;
  note?: string;
}

type CalendarItemType = 'appointment' | 'task' | 'case_date';
type CaseDateKind = 'date_of_service' | 'first_hearing_date' | 'next_date' | 'decision_date';

interface CalendarItemBase {
  type: CalendarItemType;
  dateKey: string; // YYYY-MM-DD
  title: string;
  subtitle?: string;
}

interface CalendarAppointmentItem extends CalendarItemBase {
  type: 'appointment';
  id: number;
  time?: string;
}

interface CalendarTaskItem extends CalendarItemBase {
  type: 'task';
  id: number;
}

interface CalendarCaseDateItem extends CalendarItemBase {
  type: 'case_date';
  id: number;
  kind: CaseDateKind;
}

type CalendarItem = CalendarAppointmentItem | CalendarTaskItem | CalendarCaseDateItem;

const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  
  // Calculate first and last day of the month for API query
  const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  const dateFrom = monthStart.toISOString().split('T')[0];
  const dateTo = monthEnd.toISOString().split('T')[0];

  const { data: appointmentsRes, isLoading: isLoadingAppointments } = useQuery({
    queryKey: ['appointments', dateFrom, dateTo],
    queryFn: () => appointmentsApi.list({ dateFrom, dateTo }),
  });
  const allAppointments = extractList(appointmentsRes) as Appointment[];

  const { data: tasksRes, isLoading: isLoadingTasks } = useQuery({
    queryKey: ['tasks', dateFrom, dateTo],
    queryFn: () => tasksApi.list({ date_from: dateFrom, date_to: dateTo, per_page: 200 }),
  });
  const allTasks = extractList(tasksRes) as any[];

  const { data: casesRes, isLoading: isLoadingCases } = useQuery({
    queryKey: ['court-cases', dateFrom, dateTo],
    queryFn: () => courtCasesApi.list({ dateFrom, dateTo, perPage: 200 }),
  });
  const allCases = extractList(casesRes) as any[];

  // Filter appointments for the current month
  const monthAppointments = useMemo(() => {
    return allAppointments.filter((apt: Appointment) => {
      if (!apt.date) return false;
      const aptDate = new Date(apt.date);
      return aptDate >= monthStart && aptDate <= monthEnd;
    });
  }, [allAppointments, monthStart, monthEnd]);

  const monthTasks = useMemo(() => {
    // API already filtered by range; still guard for null dates
    return allTasks.filter((t) => t && (t.start_date || t.end_date));
  }, [allTasks]);

  const caseDateItems = useMemo(() => {
    const items: CalendarCaseDateItem[] = [];
    const add = (c: any, kind: CaseDateKind, raw: any) => {
      if (!raw) return;
      const dateKey = String(raw).split('T')[0];
      if (!dateKey) return;
      if (dateKey < dateFrom || dateKey > dateTo) return;
      const labelBase = c?.case_number ? `Case ${c.case_number}` : (c?.party_name ? `Case: ${c.party_name}` : 'Case');
      const kindLabel =
        kind === 'date_of_service' ? 'service' :
        kind === 'first_hearing_date' ? 'first hearing' :
        kind === 'next_date' ? 'next date' :
        'decision';
      items.push({
        type: 'case_date',
        id: c?.id,
        kind,
        dateKey,
        title: `${labelBase} (${kindLabel})`,
        subtitle: c?.court?.court_name ?? c?.court?.name ?? undefined,
      });
    };
    allCases.forEach((c) => {
      add(c, 'date_of_service', c?.date_of_service);
      add(c, 'first_hearing_date', c?.first_hearing_date);
      add(c, 'next_date', c?.next_date);
      add(c, 'decision_date', c?.decision_date);
    });
    return items;
  }, [allCases, dateFrom, dateTo]);

  const calendarItemsByDate = useMemo(() => {
    const grouped: Record<string, CalendarItem[]> = {};

    monthAppointments.forEach((apt: Appointment) => {
      if (!apt.date) return;
      const dateKey = apt.date.split('T')[0];
      const item: CalendarAppointmentItem = {
        type: 'appointment',
        id: apt.id,
        dateKey,
        title: apt.name || 'Appointment',
        subtitle: apt.client?.name,
        time: apt.time,
      };
      (grouped[dateKey] ||= []).push(item);
    });

    monthTasks.forEach((t: any) => {
      const dateKey = (t.start_date || t.end_date || '').split('T')[0];
      if (!dateKey) return;
      const item: CalendarTaskItem = {
        type: 'task',
        id: t.id,
        dateKey,
        title: t.task_subject || 'Task',
        subtitle: t.project_status ? String(t.project_status) : undefined,
      };
      (grouped[dateKey] ||= []).push(item);
    });

    caseDateItems.forEach((it) => {
      (grouped[it.dateKey] ||= []).push(it);
    });

    // sort: appointments by time first, then tasks, then case dates
    Object.keys(grouped).forEach((k) => {
      grouped[k] = grouped[k].slice().sort((a, b) => {
        const rank = (x: CalendarItem) => (x.type === 'appointment' ? 0 : x.type === 'task' ? 1 : 2);
        const ra = rank(a);
        const rb = rank(b);
        if (ra !== rb) return ra - rb;
        if (a.type === 'appointment' && b.type === 'appointment') {
          return String(a.time ?? '').localeCompare(String(b.time ?? ''));
        }
        return a.title.localeCompare(b.title);
      });
    });

    return grouped;
  }, [monthAppointments, monthTasks, caseDateItems]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + (direction === 'next' ? 1 : -1), 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const formatTime = (time?: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const isToday = (date: Date | null) => {
    if (!date) return false;
    return date.toDateString() === today.toDateString();
  };

  const isPast = (date: Date | null) => {
    if (!date) return false;
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return dateOnly < todayOnly;
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const calendarDays = getCalendarDays();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [createModalDate, setCreateModalDate] = useState<string | null>(null);

  const selectedDateItems = selectedDate ? calendarItemsByDate[selectedDate] || [] : [];
  const isLoading = isLoadingAppointments || isLoadingTasks || isLoadingCases;

  return (
    <div className="space-y-4">
      <PageHeader
        hideTitle
        subtitle="View and manage your appointments in a beautiful calendar view."
        primaryAction={{ label: 'New Appointment', to: '/dashboard/appointments/new' }}
      />

      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeftIcon className="w-5 h-5 text-slate-700 dark:text-slate-200" />
            </button>
            <h2 className="text-2xl font-bold text-slate-700 dark:text-slate-200">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h2>
            <button
              onClick={() => navigateMonth('next')}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              aria-label="Next month"
            >
              <ChevronRightIcon className="w-5 h-5 text-slate-700 dark:text-slate-200" />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={goToToday} className="text-sm">
              Today
            </Button>
            <Link to="/dashboard/appointments">
              <Button variant="secondary" className="text-sm">View All</Button>
            </Link>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <>
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2 mb-4">
              {/* Day Headers */}
              {dayNames.map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-semibold text-slate-500 dark:text-slate-400 py-2 uppercase tracking-wide"
                >
                  {day}
                </div>
              ))}

              {/* Calendar Days */}
              {calendarDays.map((date, index) => {
                const dateKey = date ? date.toISOString().split('T')[0] : null;
                const dayItems = dateKey ? calendarItemsByDate[dateKey] || [] : [];
                const isCurrentDay = isToday(date);
                const isPastDay = isPast(date);

                return (
                  <div
                    key={index}
                    className={`
                      min-h-[100px] border border-gray-200 dark:border-gray-700 rounded-lg p-2
                      transition-all duration-200 cursor-pointer
                      ${date ? 'hover:border-primary-400' : ''}
                      ${isCurrentDay ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 border-2' : ''}
                      ${isPastDay && !isCurrentDay ? 'opacity-60 bg-gray-50 dark:bg-slate-900/30' : 'bg-white dark:bg-slate-800'}
                      ${selectedDate === dateKey ? 'ring-2 ring-primary-500 ring-offset-2' : ''}
                    `}
                    onClick={() => {
                      if (!date || !dateKey) return;
                      setSelectedDate(dateKey);
                      setCreateModalDate(dateKey);
                    }}
                  >
                    {date && (
                      <>
                        <div
                          className={`
                            text-sm font-semibold mb-1
                            ${isCurrentDay ? 'text-primary-700 dark:text-primary-300' : 'text-slate-700 dark:text-slate-200'}
                          `}
                        >
                          {date.getDate()}
                        </div>
                        <div className="space-y-1">
                          {dayItems.slice(0, 4).map((it) => {
                            const isApt = it.type === 'appointment';
                            const isTask = it.type === 'task';
                            const bg =
                              isApt
                                ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-200 hover:bg-primary-200 dark:hover:bg-primary-900/60 border-primary-500'
                                : isTask
                                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-900/50 border-amber-500'
                                  : 'bg-slate-100 dark:bg-slate-700/50 text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700/70 border-slate-500';
                            const clickTo = () => {
                              if (it.type === 'appointment') navigate(`/dashboard/appointments/${it.id}`);
                              if (it.type === 'task') navigate(`/dashboard/tasks/${it.id}`);
                              if (it.type === 'case_date') navigate(`/dashboard/court-cases/${it.id}`);
                            };
                            return (
                              <div
                                key={`${it.type}-${(it as any).id}-${(it as any).kind ?? ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  clickTo();
                                }}
                                className={`
                                  text-xs p-1.5 rounded truncate
                                  transition-colors cursor-pointer
                                  border-l-2
                                  ${bg}
                                `}
                                title={isApt ? `${it.title} - ${formatTime((it as any).time)}` : it.title}
                              >
                                <div className="font-medium truncate">{it.title}</div>
                                {isApt ? <div className="text-[10px] opacity-75">{formatTime((it as any).time)}</div> : null}
                              </div>
                            );
                          })}
                          {dayItems.length > 4 && (
                            <div className="text-xs text-slate-500 dark:text-slate-400 font-medium px-1">
                              +{dayItems.length - 4} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Selected Date Details */}
            {selectedDate && selectedDateItems.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-4 flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5" />
                  Schedule for {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Button variant="secondary" onClick={() => navigate('/dashboard/tasks/new', { state: { date: selectedDate } })}>Create Task</Button>
                  <Button variant="secondary" onClick={() => navigate('/dashboard/court-cases/new', { state: { date: selectedDate } })}>Create Case</Button>
                  <Button variant="primary" onClick={() => navigate('/dashboard/appointments/new', { state: { date: selectedDate, time: '09:00' } })}>Create Appointment</Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedDateItems.map((it) => {
                    if (it.type === 'appointment') {
                      const apt = it as CalendarAppointmentItem;
                      return (
                    <div
                      key={`apt-${apt.id}`}
                      onClick={() => navigate(`/dashboard/appointments/${apt.id}`)}
                      className="
                        p-4 rounded-lg border border-gray-200 dark:border-gray-700
                        bg-gradient-to-br from-white to-gray-50 dark:from-slate-800 dark:to-slate-900
                        hover:border-primary-400 transition-all cursor-pointer
                        group
                      "
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-semibold text-slate-700 dark:text-slate-200 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                          {apt.title}
                        </h4>
                        <span className={`
                          text-xs px-2 py-1 rounded-full font-medium
                          ${(allAppointments.find(a => a.id === apt.id)?.type) === 'new' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'}
                        `}>
                          {allAppointments.find(a => a.id === apt.id)?.type || 'N/A'}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
                        {apt.time && (
                          <div className="flex items-center gap-2">
                            <ClockIcon className="w-4 h-4" />
                            <span>{formatTime(apt.time)}</span>
                          </div>
                        )}
                        {(apt.subtitle) && (
                          <div className="flex items-center gap-2">
                            <UserIcon className="w-4 h-4" />
                            <span>{apt.subtitle}</span>
                          </div>
                        )}
                        {allAppointments.find(a => a.id === apt.id)?.is_active && (
                          <div className="flex items-center gap-2">
                            <MapPinIcon className="w-4 h-4" />
                            <span className={`
                              ${(allAppointments.find(a => a.id === apt.id)?.is_active) === 'OPEN' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}
                            `}>
                              {allAppointments.find(a => a.id === apt.id)?.is_active}
                            </span>
                          </div>
                        )}
                      </div>
                      {allAppointments.find(a => a.id === apt.id)?.note && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">{allAppointments.find(a => a.id === apt.id)?.note}</p>
                        </div>
                      )}
                    </div>
                      );
                    }
                    if (it.type === 'task') {
                      return (
                        <div
                          key={`task-${it.id}`}
                          onClick={() => navigate(`/dashboard/tasks/${it.id}`)}
                          className="
                            p-4 rounded-lg border border-gray-200 dark:border-gray-700
                            bg-gradient-to-br from-white to-gray-50 dark:from-slate-800 dark:to-slate-900
                            hover:border-amber-400 transition-all cursor-pointer
                            group
                          "
                        >
                          <h4 className="font-semibold text-slate-700 dark:text-slate-200 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                            {it.title}
                          </h4>
                          {it.subtitle ? <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">{it.subtitle}</p> : null}
                        </div>
                      );
                    }
                    // case date
                    return (
                      <div
                        key={`case-${it.id}-${(it as CalendarCaseDateItem).kind}`}
                        onClick={() => navigate(`/dashboard/court-cases/${it.id}`)}
                        className="
                          p-4 rounded-lg border border-gray-200 dark:border-gray-700
                          bg-gradient-to-br from-white to-gray-50 dark:from-slate-800 dark:to-slate-900
                          hover:border-slate-400 transition-all cursor-pointer
                          group
                        "
                      >
                        <h4 className="font-semibold text-slate-700 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                          {it.title}
                        </h4>
                        {it.subtitle ? <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">{it.subtitle}</p> : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedDate && selectedDateItems.length === 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 text-center py-8">
                <p className="text-slate-500 dark:text-slate-400 mb-4">
                  Nothing scheduled for {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  <Button variant="secondary" onClick={() => navigate('/dashboard/tasks/new', { state: { date: selectedDate } })}>Create Task</Button>
                  <Button variant="secondary" onClick={() => navigate('/dashboard/court-cases/new', { state: { date: selectedDate } })}>Create Case</Button>
                  <Button variant="primary" onClick={() => navigate('/dashboard/appointments/new', { state: { date: selectedDate, time: '09:00' } })}>Create Appointment</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {createModalDate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-slate-800 p-6 shadow-xl border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">
              Create for {new Date(createModalDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
              What would you like to schedule on this date?
            </p>
            <div className="flex flex-col gap-2 mb-4">
              <Button
                variant="secondary"
                onClick={() => {
                  navigate('/dashboard/tasks/new', { state: { date: createModalDate } });
                  setCreateModalDate(null);
                }}
              >
                Create Task
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  navigate('/dashboard/court-cases/new', { state: { date: createModalDate } });
                  setCreateModalDate(null);
                }}
              >
                Create Case
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  navigate('/dashboard/appointments/new', { state: { date: createModalDate, time: '09:00' } });
                  setCreateModalDate(null);
                }}
              >
                Create Appointment
              </Button>
            </div>
            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => setCreateModalDate(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
